import type {
  APIResponse,
  CompanySeatAssignmentsResult,
  CVSectionGenerationResult,
  GenerateCVSectionRequest,
  SubscriptionManagementUrlResult,
  SubscriptionSyncInput,
  SubscriptionStateResult,
  SubscriptionWebCheckoutInput,
  SubscriptionWebCheckoutResult,
  UpdateCompanySeatInput,
} from '@tabbio/types';
import { api } from './api';

export class SubscriptionApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'SubscriptionApiError';
  }
}

const EXPECTED_SUBSCRIPTION_SYNC_CODES = new Set([
  'RATE_LIMIT_EXCEEDED',
  'SUBSCRIPTION_BILLING_UNAVAILABLE',
]);

const TRANSIENT_SUBSCRIPTION_SYNC_ERROR_MESSAGES = [
  'failed to fetch',
  'load failed',
  'network request failed',
  'networkerror',
  'request aborted',
];

export function isExpectedSubscriptionSyncError(error: unknown) {
  if (isTransientSubscriptionSyncNetworkError(error)) {
    return true;
  }

  return (
    error instanceof SubscriptionApiError &&
    EXPECTED_SUBSCRIPTION_SYNC_CODES.has(error.code)
  );
}

export function isTransientSubscriptionSyncNetworkError(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === 'AbortError') {
    return true;
  }

  const message = error.message.toLowerCase();
  return TRANSIENT_SUBSCRIPTION_SYNC_ERROR_MESSAGES.some((needle) =>
    message.includes(needle),
  );
}

function getSubscriptionApiError<T>(response: APIResponse<T>) {
  if (!response.error) {
    return null;
  }

  return new SubscriptionApiError(
    response.error.message,
    response.error.code,
    response.meta as Record<string, unknown> | undefined,
  );
}

function withQuery(path: string, query: Record<string, string | undefined>) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value) {
      params.set(key, value);
    }
  }

  return params.size ? `${path}?${params.toString()}` : path;
}

export async function fetchSubscriptionState(input?: {
  mode?: 'seeker' | 'employer';
  companyId?: string | null;
}) {
  const response = await api.get<SubscriptionStateResult>(
    withQuery('/api/subscription/state', {
      mode: input?.mode,
      companyId: input?.companyId ?? undefined,
    }),
  );
  const error = getSubscriptionApiError(response);
  if (error) throw error;
  if (!response.data) {
    throw new Error('Subscription state returned no data');
  }
  return response.data;
}

export async function fetchCompanySeatAssignments(companyId?: string | null) {
  const response = await api.get<CompanySeatAssignmentsResult>(
    withQuery('/api/subscription/company/seats', {
      companyId: companyId ?? undefined,
    }),
  );
  const error = getSubscriptionApiError(response);
  if (error) throw error;
  if (!response.data) {
    throw new Error('Company seats returned no data');
  }
  return response.data;
}

export async function syncSubscriptionState(input?: SubscriptionSyncInput) {
  const response = await api.post<SubscriptionStateResult>('/api/subscription/sync', input ?? {});
  const error = getSubscriptionApiError(response);
  if (error) throw error;
  if (!response.data) {
    throw new Error('Subscription sync returned no data');
  }
  return response.data;
}

export async function fetchSubscriptionManagementUrl(companyId?: string | null) {
  const response = await api.get<SubscriptionManagementUrlResult>(
    withQuery('/api/subscription/management-url', {
      companyId: companyId ?? undefined,
    }),
  );
  const error = getSubscriptionApiError(response);
  if (error) throw error;
  if (!response.data) {
    throw new Error('Subscription management URL returned no data');
  }
  return response.data;
}

export async function fetchSubscriptionWebCheckoutLink(input: SubscriptionWebCheckoutInput) {
  const response = await api.post<SubscriptionWebCheckoutResult>(
    '/api/subscription/web/checkout-link',
    input,
  );
  const error = getSubscriptionApiError(response);
  if (error) throw error;
  if (!response.data) {
    throw new Error('Subscription checkout link returned no data');
  }
  return response.data;
}

export async function updateCompanySeatAssignment(
  userId: string,
  input: UpdateCompanySeatInput,
  companyId?: string | null,
) {
  const response = await api.put<CompanySeatAssignmentsResult>(
    withQuery(`/api/subscription/company/seats/${userId}`, {
      companyId: companyId ?? undefined,
    }),
    input,
  );
  const error = getSubscriptionApiError(response);
  if (error) throw error;
  if (!response.data) {
    throw new Error('Seat update returned no data');
  }
  return response.data;
}

export async function previewCvSection(input: GenerateCVSectionRequest) {
  const response = await api.post<CVSectionGenerationResult>(
    '/api/agent/cv/sections/preview',
    input,
  );
  const error = getSubscriptionApiError(response);
  if (error) throw error;
  if (!response.data) {
    throw new Error('CV section preview returned no data');
  }
  return response.data;
}
